<template>
  <div class="scout-individual">
    <div class="e-chart-container">
      <div class="title-and-chart">
        <div :id="eChartId" class="e-chart scout-chart" :style="{ height: chartHeight + 'px' }"></div>
        <div :id="eChartMiniId">
          <img :src="imageUrl" class="pdf-content-image-width hidden-dashboard-display" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EChartsMixins from "../../mixins/EChartsMixins.vue";
import BarHorizontalService from "../../services/echarts/bar.horizontal.service";

const FONT_FAMILY = "\"Franklin Gothic Demi Cond\", \"Arial Narrow\", Arial, sans-serif";
const ROW_HEIGHT = 26;
const GRID_BOTTOM = 10;
const SINGLE_HEADER_HEIGHT = 44;
const TOGETHER_LABEL_HEIGHT = 18;
const TOGETHER_ROW_HEIGHT = 22;
const PROFILE_FILLS = ["#595959", "#a6a6a6", "#f2f2f2"];
const PROFILE_TEXT_COLORS = ["#ffffff", "#ffffff", "#222222"];
const EXPECTATION_COLOR = "#1db954";
const COMPETITOR_COLOR = "#f26d21";
const COMPANY_COLOR = "#3a9d23";
const X_SYMBOL = "path://M2,0 L5,3 L8,0 L10,2 L7,5 L10,8 L8,10 L5,7 L2,10 L0,8 L3,5 L0,2 Z";
const HEADER_OFFSET = { gap: -1.25, versus: -2.5 };
const COLUMNS = {
  grade: { left: 1, width: 10 },
  need: { left: 11, width: 21 },
  wtp: { left: 33, width: 11 },
  scale: { left: 46, width: 40 },
  gap: { left: 87, width: 5 },
  versus: { left: 92, width: 7.5 }
};
const TOGETHER_COLUMNS = {
  profile: { left: 1, width: 12 },
  grade: { left: 13.5, width: 9 },
  need: { left: 23, width: 19 },
  wtp: { left: 42.5, width: 9 },
  scale: { left: 53, width: 33 },
  gap: { left: 87, width: 5 },
  versus: { left: 92, width: 7.5 }
};

export default {
  name: "IndividualScoutChart",
  mixins: [EChartsMixins],
  props: {
    scoutChart: { type: Object, required: true },
    together: { type: Boolean, default: false },
    inputReportTypeName: { type: String, default: "Chart - Scout" },
    inputDownloadFileName: { type: String, default: "Scout" }
  },
  data() {
    return {
      // the container/wrapper class is applied by the parent, once, not per individual
      addWrapper: false
    };
  },
  computed: {
    headers() {
      return Array.isArray(this.scoutChart.header) ? this.scoutChart.header : [];
    },
    needs() {
      return this.scoutChart.data || [];
    },
    columns() {
      return this.together ? TOGETHER_COLUMNS : COLUMNS;
    },
    headerHeight() {
      return this.together
        ? TOGETHER_LABEL_HEIGHT + Math.max(this.headers.length, 1) * TOGETHER_ROW_HEIGHT + 4
        : SINGLE_HEADER_HEIGHT;
    },
    gridTop() {
      return this.headerHeight + 68;
    },
    chartHeight() {
      return this.gridTop + this.needs.length * ROW_HEIGHT + GRID_BOTTOM;
    }
  },
  watch: {
    scoutChart: {
      deep: true,
      handler() {
        if (this.chart && typeof this.chart.setOption === "function") {
          this.chart.setOption(this.getChartOptions(), true);
        }
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initChart();
      window.addEventListener("resize", this.resizeChart);
    });
  },
  beforeDestroy() {
    this.cleanup();
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    cleanup() {
      window.removeEventListener("resize", this.resizeChart);
      if (this.chart && typeof this.chart.dispose === "function") {
        this.chart.dispose();
      }
    },
    resizeChart() {
      if (this.chart && typeof this.chart.resize === "function") {
        this.chart.resize();
      }
    },
    display(value) {
      return value == null || value === "" ? "--" : value;
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== "" && !Number.isNaN(Number(value));
    },
    formatDate(value) {
      if (!value) return "--";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "--" : date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    },
    formatWtp(value) {
      return this.hasValue(value) ? Number(value).toFixed(1) + "%" : "--";
    },
    parseShare(value) {
      const parsed = parseFloat(value);
      return Number.isNaN(parsed) ? null : parsed;
    },
    profileIndex(profile) {
      const profiles = [...new Set(this.needs.map(need => need.profile))];
      const index = profiles.indexOf(profile);
      return index < 0 ? 0 : index % PROFILE_FILLS.length;
    },
    buildInfoGraphics() {
      const items = header => [
        { left: 1, label: "NAME", value: this.display(header.name) },
        { left: 16, label: "COMPANY", value: this.display(header.company) },
        { left: 40, label: "EMAIL", value: this.display(header.emailAddress) },
        { left: 62, label: "COMPLETION DATE", value: this.formatDate(header.dateCompleted) },
        { left: 76, label: "STATED WTP", value: this.formatWtp(header.wtp) },
        { left: 88, label: "COMPETITOR", value: this.display(header.competitorCompany) }
      ];
      const labelStyle = { fill: "#777", font: "10px " + FONT_FAMILY };
      const text = (item, top, style, valueText) => ({
        type: "text",
        left: item.left + "%",
        top,
        z: 1,
        style: { text: valueText, ...style }
      });
      const graphics = [];
      if (!this.together) {
        const header = this.headers[0] || {};
        graphics.push({ type: "rect", left: 0, top: 0, z: 0, shape: { width: 5000, height: SINGLE_HEADER_HEIGHT }, style: { fill: "#f2f2f2" } });
        items(header).forEach(item => {
          graphics.push(text(item, 6, labelStyle, item.label));
          graphics.push(text(item, 22, { fill: "#222", font: "bold 12px " + FONT_FAMILY }, item.value));
        });
        return graphics;
      }
      graphics.push({ type: "rect", left: 0, top: 0, z: 0, shape: { width: 5000, height: TOGETHER_LABEL_HEIGHT }, style: { fill: "#f2f2f2" } });
      items({}).forEach(item => graphics.push(text(item, 3, labelStyle, item.label)));
      this.headers.forEach((header, rowIndex) => {
        const rowTop = TOGETHER_LABEL_HEIGHT + rowIndex * TOGETHER_ROW_HEIGHT;
        const fillIndex = rowIndex % PROFILE_FILLS.length;
        graphics.push({ type: "rect", left: 0, top: rowTop, z: 0, shape: { width: 5000, height: TOGETHER_ROW_HEIGHT }, style: { fill: PROFILE_FILLS[fillIndex] } });
        items(header).forEach(item => {
          graphics.push(text(item, rowTop + 4, { fill: PROFILE_TEXT_COLORS[fillIndex], font: "bold 12px " + FONT_FAMILY }, item.value));
        });
      });
      return graphics;
    },
    getChartOptions() {
      const COLUMNS = this.columns;
      const GRID_TOP = this.gridTop;
      const LEGEND_TOP = this.headerHeight + 6;
      const COLUMN_HEADER_TOP = this.headerHeight + 32;
      const needs = this.needs;
      const gridHeight = needs.length * ROW_HEIGHT;
      const shares = needs.map(need => this.parseShare(need.wtpSharePercent));
      // headroom so the label drawn at the end of the longest bar is not clipped
      const wtpMax = Math.max(35, ...shares.filter(share => share !== null)) * 1.35;
      const gradeStarts = needs
        .map((need, index) => (need.grade && (index === 0 || needs[index - 1].grade !== need.grade) ? index : -1))
        .filter(index => index >= 0);
      const groupStarts = new Set(
        needs.map((need, index) => (index > 0 && needs[index - 1].name !== need.name ? index : -1)).filter(index => index >= 0)
      );
      const columnKeys = Object.keys(COLUMNS);
      const indexOf = key => columnKeys.indexOf(key);
      const centeredKeys = ["gap", "versus"];
      const chartWidth = this.chart && typeof this.chart.getWidth === "function" ? this.chart.getWidth() : 1000;
      const profileWidthPercent = COLUMNS.profile ? COLUMNS.profile.width : 0;
      const profileRich = {};
      PROFILE_FILLS.forEach((fill, index) => {
        profileRich["p" + index] = {
          backgroundColor: fill,
          color: PROFILE_TEXT_COLORS[index],
          width: Math.max((chartWidth * profileWidthPercent) / 100 - 6, 20),
          height: ROW_HEIGHT - 2,
          padding: [0, 0, 0, 4],
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          fontWeight: "bold"
        };
      });
      const labelFor = {
        profile: need => "{p" + this.profileIndex(need.profile) + "|" + (need.profile || "") + "}",
        grade: (need, index) => (this.together || gradeStarts.includes(index) ? need.grade || "" : ""),
        need: need => need.name || "",
        gap: need => {
          if (!this.hasValue(need.valueGap)) return "--";
          const emotional = String(need.type || "").toLowerCase() === "emotional";
          return emotional ? Number(need.valueGap).toFixed(1) : String(Math.round(Number(need.valueGap)));
        },
        versus: need => need.versusCompetitor || "--"
      };

      const grids = [];
      const xAxis = [];
      const yAxis = [];
      columnKeys.forEach((key, gridIndex) => {
        const column = COLUMNS[key];
        const shaded = key === "wtp" || key === "scale";
        grids.push({
          left: column.left + "%",
          width: column.width + "%",
          top: GRID_TOP,
          height: gridHeight,
          show: shaded,
          backgroundColor: "#f4f4f4",
          borderWidth: 0
        });
        if (key === "scale") {
          xAxis.push({
            type: "value",
            gridIndex,
            min: 0,
            max: 11,
            interval: 1,
            position: "top",
            axisTick: { show: false },
            axisLine: { show: false },
            axisLabel: { color: "#333", fontSize: 11, fontFamily: FONT_FAMILY, margin: 10, formatter: value => (value >= 1 && value <= 10 ? String(value) : "") },
            splitLine: { show: true, lineStyle: { color: "#fff", width: 1 } }
          });
        } else if (key === "wtp") {
          xAxis.push({ type: "value", gridIndex, min: 0, max: wtpMax, show: false });
        } else {
          xAxis.push({ type: "value", gridIndex, min: 0, max: 1, show: false });
        }
        yAxis.push({
          type: "category",
          gridIndex,
          inverse: true,
          data: needs.map((need, index) => index),
          axisTick: { show: false },
          axisLabel: labelFor[key]
            ? {
                show: true,
                inside: !centeredKeys.includes(key),
                interval: 0,
                align: centeredKeys.includes(key) ? "center" : "left",
                margin: centeredKeys.includes(key) ? -(chartWidth * column.width) / 200 : key === "profile" ? 0 : 4,
                color: "#222",
                fontSize: key === "wtp" ? 9 : 11,
                fontFamily: FONT_FAMILY,
                fontWeight: key === "grade" ? "bold" : "normal",
                rich: key === "profile" ? profileRich : undefined,
                formatter: value => labelFor[key](needs[Number(value)], Number(value))
              }
            : { show: false },
          axisLine: { show: key === "scale", lineStyle: { color: "#333" } },
          splitLine: this.together
            ? { show: true, interval: index => groupStarts.has(index), lineStyle: { color: "#777", width: 1 } }
            : { show: false }
        });
      });

      const scaleIndex = indexOf("scale");
      const markerSeries = (name, field, symbol, symbolSize, color, z) => ({
        name,
        type: "scatter",
        xAxisIndex: scaleIndex,
        yAxisIndex: scaleIndex,
        symbol,
        symbolSize,
        itemStyle: { color },
        z,
        data: needs
          .map((need, index) => (this.hasValue(need[field]) ? { name: need.name, value: [Number(need[field]), index] } : null))
          .filter(item => item !== null)
      });

      const headerStyle = { fill: "#555", font: "bold 11px " + FONT_FAMILY };
      const header = (key, text, align = "left") => ({
        type: "text",
        left: COLUMNS[key].left + (align === "center" ? COLUMNS[key].width / 2 + (HEADER_OFFSET[key] || 0) : 0) + "%",
        top: COLUMN_HEADER_TOP,
        style: { text, textAlign: align === "center" ? "center" : "left", ...headerStyle }
      });

      const infoGraphics = this.buildInfoGraphics();

      // black separators above the first functional need, above the first emotional need, and below the last emotional need
      const isEmotional = need => String(need.type || "").toLowerCase() === "emotional";
      const firstFunctional = needs.findIndex(need => !isEmotional(need));
      const firstEmotional = needs.findIndex(isEmotional);
      const separatorRows = [];
      if (firstFunctional >= 0) separatorRows.push(firstFunctional);
      if (firstEmotional >= 0) separatorRows.push(firstEmotional, needs.length);
      const separatorGraphics = [...new Set(separatorRows)].map(row => ({
        type: "rect",
        left: 0,
        top: GRID_TOP + row * ROW_HEIGHT - 1,
        z: 10,
        silent: true,
        shape: { width: 5000, height: 1.5 },
        style: { fill: "#000" }
      }));
      // dotted line at 5.5 on the scale (1-10 axis spans 0-11), drawn in pixels from the first emotional separator to the last one
      const midpointGraphics =
        firstEmotional >= 0
          ? [
              {
                type: "line",
                left: COLUMNS.scale.left + (COLUMNS.scale.width * 5.5) / 11 + "%",
                top: GRID_TOP + firstEmotional * ROW_HEIGHT,
                z: 11,
                silent: true,
                shape: { x1: 0, y1: 0, x2: 0, y2: (needs.length - firstEmotional) * ROW_HEIGHT },
                style: { stroke: "#000", lineWidth: 2, lineDash: [2, 3] }
              }
            ]
          : [];

      return {
        animation: false,
        textStyle: { fontFamily: FONT_FAMILY },
        tooltip: {
          trigger: "item",
          formatter: params => params.name + "<br/>" + params.seriesName + ": " + params.value[0]
        },
        toolbox: { ...BarHorizontalService.getDefaultToolBox(this, this.modalId, this.inputReportTypeName, this.inputDownloadFileName), top: LEGEND_TOP - 4 },
        legend: {
          top: LEGEND_TOP,
          left: COLUMNS.scale.left + 6 + "%",
          itemWidth: 12,
          itemHeight: 12,
          textStyle: { fontSize: 10, fontFamily: FONT_FAMILY, color: "#333" },
          data: ["Expectations", "Competitor", "Company"]
        },
        graphic: [
          ...infoGraphics,
          ...separatorGraphics,
          ...midpointGraphics,
          ...(this.together ? [header("profile", "Profile")] : []),
          header("grade", "Grade"),
          header("need", "Need"),
          header("wtp", "WTP Share %"),
          header("gap", "Value\nGap", "center"),
          header("versus", "Versus\nCompetitor", "center")
        ],
        grid: grids,
        xAxis,
        yAxis,
        series: [
          {
            type: "bar",
            xAxisIndex: indexOf("wtp"),
            yAxisIndex: indexOf("wtp"),
            silent: true,
            barWidth: 16,
            itemStyle: { color: "#d4e4b8" },
            label: {
              show: true,
              position: "right",
              distance: 3,
              color: "#222",
              fontSize: 9,
              fontFamily: FONT_FAMILY,
              formatter: params => (shares[params.dataIndex] === null ? "--" : shares[params.dataIndex].toFixed(1) + "%")
            },
            data: shares.map(share => (share === null ? 0 : share))
          },
          markerSeries("Expectations", "idealValue", "rect", [4, 18], EXPECTATION_COLOR, 3),
          markerSeries("Competitor", "competitorValue", "circle", 11, COMPETITOR_COLOR, 4),
          markerSeries("Company", "actualValue", X_SYMBOL, 12, COMPANY_COLOR, 5)
        ]
      };
    }
  }
};
</script>

<style scoped>
.scout-individual { margin-bottom: 40px; font-family: "Franklin Gothic Demi Cond", "Arial Narrow", Arial, sans-serif; }
.scout-chart { width: 100%; min-width: 900px; }
.scout-individual .e-chart-container,
.scout-individual .title-and-chart { width: 100%; flex-direction: column; }
</style>
